import {
	Tabs,
	Tab,
	Column,
	Flex,
	ButtonSelect,
	Block,
	Button,
	Icon,
	Title,
	Text,
	DropDownMenu,
	Tooltip
} from '@upsales/components';
import EditPhonecallNotes from 'Components/EditPhonecall/EditPhonecallNotes';
import InlineAction from 'Components/Dialogs/InlineAction/InlineAction';
import AppointmentOutcome from 'App/resources/Model/AppointmentOutcome';
import AppointmentSidebarCalendar from './AppointmentSidebarCalendar';
import { APPOINTMENT_ACTIONS } from 'App/babel/enum/activityOutcome';
import AlertBody from 'App/babel/components/Dialogs/Body/AlertBody';
import { Speed } from 'App/components/animations/Animate/Animate';
import FilesTabContent from '../FilesTabContent/FilesTabContent';
import PopupPortalAnchors from 'App/helpers/PopupPortalAnchors';
import PreMeetingResearchView from './PreMeetingResearchView';
import buildTitle from './AppointmentController/buildTitle';
import { Addon } from 'Store/actions/FeatureHelperActions';
import {
	hasSoftDeployAccess,
	isAvailableAddon,
	isAvailable as isFeatureAvailable,
	Feature
} from 'App/services/FeatureHelperStandalone';
import { ThirdButton } from '@upsales/components/Buttons';
import AISummaryResource from 'App/resources/AISummary';
import { SlideFade } from 'App/components/animations';
import MeetingDebriefView from './MeetingDebriefView';
import AppointmentTodos from './AppointmentTodos';
import Sidebar from './AppointmentModalSidebar';
import RelateTo from 'App/components/RelateTo';
import Fields from './AppointmentModalFields';
import Footer from './AppointmentModalFooter';
import AINotesDisplay from './AINotesDisplay';
import ModalHeader from '../ModalHeader';
import logError from 'Helpers/logError';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import React from 'react';
import AppService from 'App/legacy/AppService';

const TAB = {
	DETAILS: 'details',
	FILES: 'files',
	INTERNAL_SUMMARY: 'internal_summary',
	PRE_MEETING_RESEARCH: 'pre_meeting_research'
};

const MAX_ITERATIONS = 1000;

const propTypes = {
	account: PropTypes.shape({
		userEditable: PropTypes.bool
	}),
	className: PropTypes.string,
	id: PropTypes.string,
	appointment: PropTypes.object.isRequired,
	actions: PropTypes.object.isRequired,
	dateAvailability: PropTypes.instanceOf(Date),
	standardFields: PropTypes.object.isRequired,
	savingType: PropTypes.string,
	hasDocumentTemplates: PropTypes.bool,
	documentTemplates: PropTypes.array,
	isAvailable: PropTypes.object,
	saving: PropTypes.bool,
	created: PropTypes.bool,
	hideOutcome: PropTypes.bool,
	requiredFields: PropTypes.object.isRequired,
	fieldErrors: PropTypes.object.isRequired,
	promptRemoval: PropTypes.bool,
	outcomeAnswered: PropTypes.bool,
	currentAppointmentLength: PropTypes.number,
	contactOpened: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	isDirty: PropTypes.bool,
	parentElem: PropTypes.object,
	invitedContacts: PropTypes.object,
	invitedEmailAttendees: PropTypes.object,
	existingAppointment: PropTypes.object
};

class AppointmentModal extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			selectedTab: TAB.DETAILS,
			conflicting: [],
			files: [],
			newFiles: [],
			deletedFiles: [],
			allOtherAppointments: [],
			externalEvents: {
				events: [],
				otherUsers: {}
			},
			isCancelled: props.appointment?.outcomeType === APPOINTMENT_ACTIONS.CANCELLED,
			showCalendar: !props.id && !props.appointment?.id,
			availabilityTableVisible: false,
			dateAvailability: props.dateAvailability,
			showInlineAction: 'none',
			notesFilterActive: false,
			showSelectOpportunity: false,
			selectedNotesType: 'regular', // 'regular' or 'ai'
			isCreatingPreMeetingResearch: false,
			selectedSummaryLanguage: 'native', // 'native' or 'en'
			meetingDebrief: null, // { summary, summaryEn } from AISummary entity
			preMeetingResearch: null // { summary, summaryEn } from AISummary entity
		};

		this.openContact = this.openContact.bind(this);
		this.timelineClick = this.timelineClick.bind(this);
		this.checkConflicting = this.checkConflicting.bind(this);

		this.saveOpportunityTimeout = undefined;

		this.loggedInUserId = AppService.getSelf().id;
		this.loggedInCustomerId = AppService.getCustomerId();
		this.calendarIntegrations = AppService.getCalendarIntegrations();
		this.hasTodoList = hasSoftDeployAccess('TODO_LIST');
		this.wrapperRef = React.createRef();
		this.hasDocuments = isFeatureAvailable(Feature.DOCUMENTS);
		this.hasSupport = isFeatureAvailable(Feature.CUSTOMER_SUPPORT);
		this.hasPreMeetingResearch = isAvailableAddon(Addon.AI_PRE_MEETING_RESEARCH);
	}

	async getExternalEventsAndFindOverlap(date, userIds, appointment) {
		let events = [];
		const otherUsers = {};
		if (!this.calendarIntegrations.length) {
			return { conflictingEvent: null, events };
		}

		try {
			for (const integration of this.calendarIntegrations) {
				const res = await Tools.StandardIntegration.data(this.loggedInCustomerId).run({
					data: {
						startDateTime: moment.utc(date.start).format(),
						endDateTime: moment.utc(date.end).format()
					},
					userIds,
					type: 'events',
					integrationId: integration.id
				});

				if (res.data) {
					if (res.data.events) {
						events = res.data.events;
					}
					if (res.data.otherUsers) {
						for (const userId in res.data.otherUsers) {
							otherUsers[userId] = res.data.otherUsers[userId];
						}
					}
				}
			}
		} catch (e) {
			console.error('Could not fetch external events', e);
			return { conflictingEvent: null, events };
		}

		const dateStart = moment(date.start);
		const dateEnd = moment(date.end).subtract(1, 'minute');
		// Formula source: https://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap
		const dateRangesOverlap = (start, end) =>
			moment.max([moment(start).subtract(1, 'minute'), dateStart]) < moment.min([moment(end), dateEnd]);
		const isThisAppointment = e =>
			(appointment.id && appointment.id === e.upsalesId) || (e.id && e.id === appointment.externalCalendarId);
		const isConflicting = events =>
			events.find(e => dateRangesOverlap(e.startDateTime, e.endDateTime) && !isThisAppointment(e));

		const conflictingEvent = { users: [] };
		if (isConflicting(events)) {
			conflictingEvent.users.push({ id: this.loggedInUserId });
		}

		for (const userId in otherUsers) {
			if (isConflicting(otherUsers[userId])) {
				conflictingEvent.users.push({ id: parseInt(userId) });
			}
		}

		return {
			conflictingEvent,
			otherUsers,
			events
		};
	}

	getAppointments(date, userIds, appointmentId) {
		const rb = new Tools.RequestBuilder();
		const AppointmentAttributes = Tools.Appointment.attr;

		rb.addFilter(AppointmentAttributes.users, rb.comparisonTypes.Equals, userIds);
		if (appointmentId) {
			rb.addFilter(AppointmentAttributes.id, rb.comparisonTypes.NotEquals, appointmentId);
		}
		// Get overlapping appointments
		rb.addFilter(AppointmentAttributes.date, rb.comparisonTypes.LessThan, date.end);
		rb.addFilter(AppointmentAttributes.endDate, rb.comparisonTypes.GreaterThan, date.start);

		rb.addFilter(AppointmentAttributes.outcome, rb.comparisonTypes.Equals, [
			AppointmentOutcome.COMPLETED,
			AppointmentOutcome.PLANNED
		]);
		return Tools.Appointment.customer(this.loggedInCustomerId).find(rb.build());
	}

	getAllAppointments(props) {
		const { dateAvailability, currentAppointmentLength, appointment } = props;

		const appointmentDate = {
			start: moment(dateAvailability).toDate(),
			end: moment(dateAvailability).add(currentAppointmentLength, 'minutes').toDate()
		};

		const appointmentDay = {
			start: moment(appointmentDate.start).startOf('day').toDate(),
			end: moment(appointmentDate.end).endOf('day').toDate()
		};

		const userIds = appointment.users.map(o => o.id);

		Promise.all([
			this.getAppointments(appointmentDate, userIds, appointment.id),
			this.getExternalEventsAndFindOverlap(appointmentDate, userIds, appointment),
			this.getAppointments(appointmentDay, userIds, appointment.id)
		])
			.then(res => {
				const conflicting = res[0].data;
				const external = res[1];
				const otherAppointments = this.addNonDuplicateExternalEvents(res[2].data, external);

				if (external.conflictingEvent?.users?.length) {
					conflicting.push(external.conflictingEvent);
				}

				const isNew = !appointment.id;

				this.setState({
					conflicting,
					changingDate: false,
					otherAppointments,
					allOtherAppointments: res[2].data,
					externalEvents: external,
					dateAvailability,
					availabilityTableVisible:
						isNew && conflicting.length > 0 ? true : this.state.availabilityTableVisible
				});
			})
			.catch(e => {
				console.error('Error while getting appointments:', e);
				this.setState({
					changingDate: false
				});
			});
	}

	componentDidMount() {
		this.getAllAppointments(this.props);
		this.loadFiles();
		this.loadMeetingDebrief();
		this.loadPreMeetingResearch();
	}

	async loadMeetingDebrief() {
		const { appointment } = this.props;

		if (!appointment?.id) {
			return;
		}

		try {
			const meetingDebrief = await AISummaryResource.getMeetingDebriefInternal(appointment.id);

			if (meetingDebrief) {
				const meetingPassed = moment(appointment?.date).isBefore(moment());
				this.setState({
					meetingDebrief,
					selectedTab: meetingPassed ? TAB.INTERNAL_SUMMARY : this.state.selectedTab
				});
			}
		} catch (error) {
			console.error('Failed to load meeting debrief:', error);
		}
	}

	async loadPreMeetingResearch() {
		const { appointment } = this.props;

		if (!appointment?.id || !this.hasPreMeetingResearch) {
			return;
		}

		try {
			const preMeetingResearch = await AISummaryResource.getPreMeetingResearch(appointment.id);

			if (preMeetingResearch) {
				const meetingPassed = moment(appointment?.date).isBefore(moment());
				this.setState({
					preMeetingResearch,
					selectedTab: !meetingPassed ? TAB.PRE_MEETING_RESEARCH : this.state.selectedTab
				});
			}
		} catch (error) {
			console.error('Failed to load pre-meeting research:', error);
		}
	}

	componentWillUnmount() {
		if (this.saveOpportunityTimeout) {
			clearTimeout(this.saveOpportunityTimeout);
		}
		if (this.reloadModalPosition) {
			clearTimeout(this.reloadModalPosition);
		}
	}

	dateCompare(date1, date2) {
		const time1 = date1 instanceof Date && date1.getTime();
		const time2 = date2 instanceof Date && date2.getTime();
		return time1 === time2;
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(nextProps) {
		const dateAvailabilityChanged = !this.dateCompare(this.props.dateAvailability, nextProps.dateAvailability);
		const usersChanged =
			JSON.stringify(this.props.appointment.users) !== JSON.stringify(nextProps.appointment.users);

		if (dateAvailabilityChanged || usersChanged) {
			this.getAllAppointments(nextProps);
		}
	}

	loadFiles() {
		const { appointment } = this.props;
		const fileFilter = new Tools.RequestBuilder();

		if (!appointment.id) {
			return;
		}

		fileFilter.addFilter(
			Tools.File.attr.entity,
			fileFilter.comparisonTypes.Equals,
			Tools.File.entityTypes.APPOINTMENT
		);
		fileFilter.addFilter(Tools.File.attr.entityId, fileFilter.comparisonTypes.Equals, appointment.id);

		Tools.File.customer(this.loggedInCustomerId)
			.find(fileFilter.build())
			.then(response => {
				this.setState({ files: response.data });
			})
			.catch(err => console.error('Error while loading files:', err));
	}

	openContact(user) {
		const { contactOpened, actions } = this.props;

		if (contactOpened === user.id) {
			actions.setContactOpened(null);
		} else {
			actions.setContactOpened(user.id);
		}
	}

	addNonDuplicateExternalEvents(appointments, { events, otherUsers }) {
		const otherAppointments = [...appointments];

		const addIfNotFound = (userId, events) => {
			events.forEach(event => {
				if (
					!appointments.find(
						o =>
							moment(o.date).isSame(event.startDateTime) &&
							moment(o.endDate).isSame(event.endDateTime) &&
							o.users.find(u => u.id === userId)
					)
				) {
					otherAppointments.push({
						date: event.startDateTime,
						endDate: event.endDateTime,
						users: [{ id: userId }]
					});
				}
			});
		};

		addIfNotFound(this.loggedInUserId, events);

		for (const userId in otherUsers) {
			addIfNotFound(parseInt(userId), otherUsers[userId]);
		}

		return otherAppointments;
	}

	timelineClick(hoverDate) {
		if (this.props.appointment.userEditable) {
			this.props.actions.changeStartDate(moment(hoverDate), { setBoth: true });
		}
	}

	checkConflicting() {
		const state = Object.assign({}, this.state);

		if (state.availabilityTableVisible) {
			state.availabilityTableVisible = false;
		} else {
			state.availabilityTableVisible = true;
		}

		this.setState(state);
	}

	handleTabChange = selectedTab => {
		this.reloadModalPosition = setTimeout(() => this.props.actions.reloadModalPosition(), 50);

		this.setState({ selectedTab });
	};

	handleSummaryLanguageChange = selectedSummaryLanguage => {
		this.setState({ selectedSummaryLanguage });
	};

	handleFileDrop = files => {
		try {
			const existingFiles = [...this.state.files, ...this.state.newFiles];
			const renameOnCollision = input => {
				const hasExtension = input.includes('.');
				let baseName, extension;

				if (hasExtension) {
					const asSplit = input.split('.');
					extension = asSplit.pop();
					baseName = asSplit.join('.');
				} else {
					baseName = input;
					extension = '';
				}

				let newName = input;
				let i = 1;
				while (existingFiles.find(f => f.filename === newName || f.name === newName)) {
					newName = hasExtension ? `${baseName}-${i}.${extension}` : `${baseName}-${i}`;
					i++;
					if (i > MAX_ITERATIONS) {
						throw new Error('Max iterations reached');
					}
				}
				return newName;
			};

			// This will NOT take into consideration if the user is able to upload multiple files with the same name **in the same drop**
			// however, for a user to upload multiple files in the same drop, they would need to have multiple files with the same name in their filesystem
			// which doesn't seem likely for them to have
			const mappedFiles = files.map(file => {
				const finalFilename = renameOnCollision(file.name);
				return new File([file], finalFilename, {
					type: file.type,
					lastModified: file.lastModified
				});
			});

			this.setState({ newFiles: [...this.state.newFiles, ...mappedFiles] });
		} catch (e) {
			logError(e);
			// Dumb fallback to the previous behavior
			this.setState(({ newFiles }) => ({ newFiles: [...newFiles, ...files] }));
		}
	};

	handleFileRemoval = file => {
		if (file.id) {
			const { files, deletedFiles } = this.state;

			deletedFiles.push(file);
			files.splice(files.indexOf(file), 1);

			this.setState({ files, deletedFiles });
		} else {
			const { newFiles } = this.state;
			newFiles.splice(newFiles.indexOf(file), 1);

			this.setState({ newFiles });
		}
	};

	saveAppointment = inviteContacts => {
		const { newFiles, deletedFiles } = this.state;
		this.props.actions
			.saveAppointmentWithFiles(newFiles, deletedFiles, inviteContacts)
			.then(({ openSelectOpportunity }) => {
				if (openSelectOpportunity) {
					setTimeout(this.openSelectOpportunity, 1500);
				}
			})
			.catch(err => console.error(err));
	};

	saveSelectedOpportunity = v => {
		this.saveOpportunityTimeout = setTimeout(() => {
			if (v && v.opportunityId) {
				this.props.actions.saveSelectedOpportunity({ id: v.opportunityId });
			} else {
				this.props.actions.resolve(this.props.appointment);
			}
		}, Speed.normal);
	};

	openSelectOpportunity = () => {
		this.setState({ showSelectOpportunity: true });
	};

	renderDragOverlay() {
		return (
			<div className="drag-overlay">
				<img src="/img/drop-file.png" alt="Drop a file" />
				<div>{Tools.$translate('file.dropToUpload')}</div>
			</div>
		);
	}

	toggleInlineAction = position => {
		this.setState({ showInlineAction: position });
	};

	cancelAppointment = () => {
		this.props.actions.changeAppointmentParam('outcome', AppointmentOutcome.NOT_COMPLETED);
		this.props.actions.changeAppointmentParam('outcomeType', APPOINTMENT_ACTIONS.CANCELLED);
		this.setState({ isCancelled: true });
	};

	closeModal = () => {
		const { actions, isDirty } = this.props;
		const { showSelectOpportunity } = this.state;

		if (isDirty) {
			this.toggleInlineAction('top');
			return;
		}

		if (showSelectOpportunity) {
			this.saveSelectedOpportunity();
		} else {
			actions.close();
		}
	};

	handleNotesTypeChange = selectedNotesType => {
		this.setState({ selectedNotesType });
	};

	startPreMeetingResearch = async closeDropdown => {
		const { appointment } = this.props;

		closeDropdown();
		try {
			await Tools.Appointment.customer(this.loggedInCustomerId).startPreMeetingResearch(appointment.id);

			Tools.NotificationService.addNotification({
				style: Tools.NotificationService.style.INFO,
				icon: 'info-circle',
				title: 'default.info',
				body: 'default.aiPreMeetingResearchStarted'
			});
		} catch (error) {
			Tools.NotificationService.addNotification({
				style: Tools.NotificationService.style.ERROR,
				icon: 'times',
				title: 'default.error',
				body: error.message || 'default.aiPreMeetingResearchFailed'
			});
		} finally {
			// For V1, don't set it to false to avoid multiple jobs on accident as long as the same "modal session" is open
			this.setState({ isCreatingPreMeetingResearch: true });
		}
	};

	renderResearchDropdown = closeDropdown => {
		return (
			<div className="ResearchConfirm">
				<Flex direction="column" gap="u4" space="pbl pll ptl prl">
					<Title size="sm" bold>
						{Tools.$translate('default.createPreMeetingResearch')}
					</Title>
					<Text color="grey-13">{Tools.$translate('default.createPreMeetingResearchConfirm')}</Text>
					<Flex gap="u2" space="mtm" justifyContent="flex-end">
						<Button
							onClick={() => this.startPreMeetingResearch(closeDropdown)}
							color="green"
							loading={this.state.isCreatingPreMeetingResearch}
						>
							{Tools.$translate('default.create')}
						</Button>
						<ThirdButton
							onClick={closeDropdown}
							color="grey"
							disabled={this.state.isCreatingPreMeetingResearch}
						>
							{Tools.$translate('cancel')}
						</ThirdButton>
					</Flex>
				</Flex>
			</div>
		);
	};

	renderNotesSection = (appointment, actions, fieldErrors) => {
		const { selectedNotesType } = this.state;
		const hasAiNotes = appointment.aiNotes && appointment.aiNotes.trim().length > 0;

		// If there are no AI notes, just show regular notes without toggle
		if (!hasAiNotes) {
			return (
				<EditPhonecallNotes
					notes={appointment.notes}
					onChange={value => {
						actions.changeAppointmentParam('notes', value);
					}}
					onAutoSave={
						appointment?.id > 0
							? value => this.props.actions.saveAppointmentNotes('notes', value)
							: undefined
					}
					textAreaProps={{
						required: this.props.requiredFields.Notes,
						disabled: !appointment.userEditable
					}}
					autosave={true}
					hasError={fieldErrors.Notes}
					textareaTooltipText={AppService.getMetadata().standardFields.Appointment.Notes.tooltip}
				/>
			);
		}

		// Show toggle and appropriate notes component
		return (
			<Flex direction="column" flex={1} gap={'u4'}>
				<Block className="NotesTypeToggleContainer">
					<ButtonSelect
						size="sm"
						value={selectedNotesType}
						onChange={this.handleNotesTypeChange}
						options={[
							{
								value: 'regular',
								title: Tools.$translate('default.notes')
							},
							{
								value: 'ai',
								title: Tools.$translate('default.aiSummary')
							}
						]}
					/>
				</Block>
				{/* Keeping it duplicated because we may want to change the layout a little bit later */}
				{selectedNotesType === 'regular' ? (
					<EditPhonecallNotes
						notes={appointment.notes}
						onChange={value => {
							actions.changeAppointmentParam('notes', value);
						}}
						onAutoSave={
							appointment?.id > 0
								? value => this.props.actions.saveAppointmentNotes('notes', value)
								: undefined
						}
						textAreaProps={{
							required: this.props.requiredFields.Notes,
							disabled: !appointment.userEditable
						}}
						autosave={true}
						hasError={fieldErrors.Notes}
						textareaTooltipText={AppService.getMetadata().standardFields.Appointment.Notes.tooltip}
					/>
				) : (
					<AINotesDisplay
						notes={appointment.aiNotes}
						notesEn={appointment.aiNotesEn}
						onChange={(field, value) => {
							actions.changeAppointmentParam(field, value);
							if (appointment?.id > 0) {
								this.props.actions.saveAppointmentNotes(field, value);
							}
						}}
						disabled={!appointment.userEditable}
					/>
				)}
			</Flex>
		);
	};

	renderModal() {
		const { appointment, actions, standardFields, fieldErrors, account } = this.props;
		const {
			selectedTab,
			files,
			newFiles,
			showSelectOpportunity,
			selectedSummaryLanguage,
			meetingDebrief,
			preMeetingResearch
		} = this.state;

		// Check if we should show language toggle for summary (both exist and are different)
		const hasNativeSummary = !!meetingDebrief?.summary;
		const hasEnglishSummary = !!meetingDebrief?.summaryEn;
		const showSummaryLanguageToggle =
			hasNativeSummary &&
			hasEnglishSummary &&
			JSON.stringify(meetingDebrief.summary) !== JSON.stringify(meetingDebrief.summaryEn);

		const hasNativePreMeetingResearch = !!preMeetingResearch?.summary;
		const hasEnglishPreMeetingResearch = !!preMeetingResearch?.summaryEn;
		const showPreMeetingLanguageToggle =
			hasNativePreMeetingResearch &&
			hasEnglishPreMeetingResearch &&
			JSON.stringify(preMeetingResearch.summary) !== JSON.stringify(preMeetingResearch.summaryEn);

		const meetingPassed = moment(appointment?.date).isBefore(moment());

		const onChangeDate = next => {
			const date = this.state.dateAvailability;
			const newDate = moment(date).add(next ? 1 : -1, 'days');

			this.setState({ changingDate: true });

			this.props.actions.changeStartDate(newDate, { setBoth: true });
		};

		return (
			<>
				{this.state.showInlineAction === 'top' ? (
					<InlineAction
						toggleInlineAction={this.toggleInlineAction}
						onReject={actions.discardFromInline}
						onConfirm={() => this.saveAppointment(false)}
						showTop
					/>
				) : null}

				<ModalHeader
					icon="fa fa-calendar"
					className="white-header"
					title={buildTitle({ Tools, appointment, actions })}
					appointmentId={appointment.id}
					close={this.closeModal}
				/>

				<SlideFade visible={showSelectOpportunity} direction="bottom" speed="slow" complete>
					<RelateTo
						className="relate-to"
						entityLabel={'default.appointment'}
						client={account}
						contact={appointment}
						availableEntities={{ opportunity: true, activity: false, appointment: false }}
						user={{ id: this.loggedInUserId }}
						visible={showSelectOpportunity}
						onChange={this.saveSelectedOpportunity}
					></RelateTo>
				</SlideFade>

				<Dropzone onDrop={this.handleFileDrop} noClick disabled={!this.hasDocuments}>
					{({ getRootProps, getInputProps, isDragActive }) => (
						<div
							{...{
								...getRootProps({
									className: classNames(
										'up-modal-content has-sidebar-x2 dropzone appointment-content',
										{
											'drag-active': isDragActive
										}
									)
								}),
								onBlur: () => null,
								onClick: () => null,
								onFocus: () => null,
								onKeyDown: () => null,
								tabIndex: null
							}}
						>
							<input {...getInputProps()} />
							{isDragActive && this.renderDragOverlay()}
							<Flex direction="column" className="appointment-sidebar">
								<Flex justifyContent="center" space="mtm mbm">
									<ButtonSelect
										size="sm"
										value={this.state.showCalendar ? 'availability' : 'participants'}
										onChange={val => this.setState({ showCalendar: val === 'availability' })}
										options={[
											{
												value: 'participants',
												title: Tools.$translate('default.participantsClean')
											},
											{
												value: 'availability',
												title: Tools.$translate('default.availability')
											}
										]}
									/>
								</Flex>
								{this.state.showCalendar ? (
									<AppointmentSidebarCalendar
										appointment={appointment}
										onChangeDate={onChangeDate}
										onChangeTime={this.timelineClick}
										loading={this.state.changingDate}
										date={this.state.dateAvailability}
										appointments={this.state.allOtherAppointments}
										externalEvents={this.state.externalEvents}
									/>
								) : (
									<Sidebar
										{...this.props}
										{...this.state}
										actions={actions}
										openContact={this.openContact}
										activeContact={this.props.contactOpened}
										checkConflicting={this.checkConflicting}
										account={account}
										hasSupport={this.hasSupport}
									/>
								)}
							</Flex>
							<div className="flex-container">
								<Column className="AppointmentTabs" id="AppointmentTabs">
									<div className="AppointmentTabs__TabsContainer">
										<Flex justifyContent="space-between" alignItems="center">
											<Tabs
												color="white"
												selected={selectedTab}
												onChange={this.handleTabChange}
												noFlex={true}
											>
												{this.hasPreMeetingResearch &&
												!meetingPassed &&
												(preMeetingResearch?.summary || preMeetingResearch?.summaryEn) ? (
													<Tab
														id={TAB.PRE_MEETING_RESEARCH}
														title={Tools.$translate('preMeetingResearch.tabTitle')}
													/>
												) : null}
												{meetingDebrief?.summary || meetingDebrief?.summaryEn ? (
													<Tab
														id={TAB.INTERNAL_SUMMARY}
														title={Tools.$translate('meetingDebrief.internalSummary')}
													/>
												) : null}
												<Tab id={TAB.DETAILS} title={Tools.$translate('socialEvent.details')} />
												<Tab
													id={TAB.FILES}
													title={`${Tools.$translate('file.files')} (${
														files.length + newFiles.length
													})`}
												/>
											</Tabs>
											{this.hasPreMeetingResearch &&
												showPreMeetingLanguageToggle &&
												selectedTab === TAB.PRE_MEETING_RESEARCH && (
													<Flex spacing="mr4">
														<ButtonSelect
															size="sm"
															style={{ width: '170px' }}
															value={selectedSummaryLanguage}
															onChange={this.handleSummaryLanguageChange}
															options={[
																{
																	value: 'native',
																	title: Tools.$translate(
																		'preMeetingResearch.nativeLanguage'
																	)
																},
																{
																	value: 'en',
																	title: Tools.$translate('language.english')
																}
															]}
														/>
													</Flex>
												)}
											{showSummaryLanguageToggle && selectedTab === TAB.INTERNAL_SUMMARY && (
												<Flex spacing="mr4">
													<ButtonSelect
														size="sm"
														style={{ width: '170px' }}
														value={selectedSummaryLanguage}
														onChange={this.handleSummaryLanguageChange}
														options={[
															{
																value: 'native',
																title: Tools.$translate('meetingDebrief.nativeLanguage')
															},
															{
																value: 'en',
																title: Tools.$translate('language.english')
															}
														]}
													/>
												</Flex>
											)}
										</Flex>
									</div>

									<div
										className={classNames('AppointmentTabs__TabsContent', {
											'AppointmentTabs__TabsContent--active': TAB.DETAILS === selectedTab
										})}
									>
										<Fields
											standardFields={standardFields}
											fieldErrors={fieldErrors}
											{...this.props}
											{...this.state}
											contactOpened={this.props.contactOpened}
											openContact={this.openContact}
										/>
									</div>
									<div
										className={classNames('AppointmentTabs__TabsContent', {
											'AppointmentTabs__TabsContent--active': TAB.FILES === selectedTab
										})}
									>
										<FilesTabContent
											files={files}
											newFiles={newFiles}
											onDrop={this.handleFileDrop}
											onRemove={this.handleFileRemoval}
										/>
									</div>
									{this.hasPreMeetingResearch &&
									!meetingPassed &&
									(preMeetingResearch?.summary || preMeetingResearch?.summaryEn) ? (
										<div
											className={classNames('AppointmentTabs__TabsContent', {
												'AppointmentTabs__TabsContent--active':
													TAB.PRE_MEETING_RESEARCH === selectedTab
											})}
										>
											<PreMeetingResearchView
												research={
													selectedSummaryLanguage === 'en' && preMeetingResearch?.summaryEn
														? preMeetingResearch.summaryEn
														: preMeetingResearch.summary
												}
												forceEnglish={selectedSummaryLanguage === 'en'}
											/>
										</div>
									) : null}
									{meetingDebrief?.summary || meetingDebrief?.summaryEn ? (
										<div
											className={classNames('AppointmentTabs__TabsContent', {
												'AppointmentTabs__TabsContent--active':
													TAB.INTERNAL_SUMMARY === selectedTab
											})}
										>
											<MeetingDebriefView
												debrief={
													selectedSummaryLanguage === 'en' && meetingDebrief?.summaryEn
														? meetingDebrief.summaryEn
														: meetingDebrief.summary
												}
												forceEnglish={selectedSummaryLanguage === 'en'}
											/>
										</div>
									) : null}
								</Column>
							</div>

							{selectedTab !== TAB.INTERNAL_SUMMARY &&
							(!this.hasPreMeetingResearch || selectedTab !== TAB.PRE_MEETING_RESEARCH) ? (
								<Column className={'appointment-notes appointment-history-log'}>
									{this.hasTodoList && appointment.id ? (
										<AppointmentTodos
											appointment={this.props.appointment}
											canCreate={appointment.userEditable}
										/>
									) : null}

									{isAvailableAddon(Addon.AI_PRE_MEETING_RESEARCH) &&
									appointment.id &&
									appointment.client?.id ? (
										<Flex>
											<DropDownMenu
												className="start-research-button-dropdown"
												align="left"
												renderTrigger={(expanded, setExpanded) => {
													const hasMeetingPassed = moment(appointment.date).isBefore(
														moment()
													);
													const isDisabled =
														this.state.isCreatingPreMeetingResearch ||
														this.props.saving ||
														hasMeetingPassed;

													let tooltipMessage;
													if (hasMeetingPassed) {
														tooltipMessage = Tools.$translate(
															'default.createPreMeetingResearchDisabledMeetingPassed'
														);
													} else if (this.state.isCreatingPreMeetingResearch) {
														tooltipMessage = Tools.$translate(
															'default.createPreMeetingResearchDisabledAlreadyCreating'
														);
													}

													return (
														<Tooltip title={tooltipMessage} disabled={!tooltipMessage}>
															<Button
																key="start-research-button"
																color="super-duper-light-purple"
																disabled={isDisabled}
																onClick={setExpanded}
																className="start-research-button"
																shadow="none"
															>
																<Icon name="stars" space="mrs" />
																{Tools.$translate('default.createPreMeetingResearch')}
															</Button>
														</Tooltip>
													);
												}}
											>
												{closeDropdown => this.renderResearchDropdown(closeDropdown)}
											</DropDownMenu>
										</Flex>
									) : null}

									{this.renderNotesSection(appointment, actions, fieldErrors)}
								</Column>
							) : null}
						</div>
					)}
				</Dropzone>

				<Footer
					savingType={this.props.savingType}
					setSavingType={actions.setSavingType}
					dirty={this.props.isDirty}
					appointment={appointment}
					hasDocumentTemplates={this.props.hasDocumentTemplates}
					documentTemplates={this.props.documentTemplates}
					isAvailable={this.props.isAvailable}
					actions={actions}
					remove={() => actions.promptRemoval({})}
					saveAppointment={this.saveAppointment}
					created={this.props.created}
					cancel={this.cancelAppointment}
					followUp={isActivity => actions.createFollowUp(isActivity)}
					close={() => actions.close()}
					onDrop={this.handleFileDrop}
					saving={this.props.saving}
					toggleInlineAction={value => this.toggleInlineAction(value)}
					showInlineAction={this.state.showInlineAction}
					discard={() => actions.discardFromInline()}
					invitedContacts={this.props.invitedContacts}
					invitedEmailAttendees={this.props.invitedEmailAttendees}
					existingAppointment={this.props.existingAppointment}
				/>
				{this.props.promptRemoval ? (
					<div className="fullmodal-container">
						<div className="fullscreen-content up-modal-content">
							<AlertBody entity="appointment" numSelected={1} />
							<div className="fullmodal-control">
								<button
									className="btn up-btn btn-lg btn-red btn-block"
									onClick={() => actions.deleteAppointment()}
								>
									{Tools.$translate('default.delete')}
								</button>
								<button
									className="btn up-btn btn-link btn-grey btn-block"
									onClick={() => actions.promptRemoval({ closePrompt: true })}
								>
									{Tools.$translate('cancel')}
								</button>
							</div>
						</div>
					</div>
				) : null}
			</>
		);
	}

	render() {
		const { id, className } = this.props;

		return (
			<div id={id || 'appointment-modal'} className={`appointment-modal ${className}`} ref={this.wrapperRef}>
				<PopupPortalAnchors anchor={this.wrapperRef.current} scrollContainerSelector=".AppointmentTabs">
					{this.renderModal()}
				</PopupPortalAnchors>
			</div>
		);
	}
}

AppointmentModal.propTypes = propTypes;
window.AppointmentModal = AppointmentModal;
export default window.AppointmentModal;
